import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import type { MatDialogRef } from '@angular/material/dialog';
import type { MatSelectChange } from '@angular/material/select';
import type { IBaseDepartment, IDepartments } from '@shared/services/department.service';

import type { IMesUser, IMesUsers } from '@shared/services/mesuser.service';
import type { ITeamProfile, ITeamProfiles } from '@shared/services/teamProfiles.service';
import type { IWorktask, IWorktasks } from '@shared/services/worktask.service';
import type { UUID } from 'node:crypto';

import type { IShiftModel, ITimestamp, ShiftDateBasis } from './mes-shift.service';

import { animate, state, style, transition, trigger } from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';

import { MatTableDataSource } from '@angular/material/table';

import { TranslateService } from '@ngx-translate/core';
import { ConfirmdialogComponent } from '@shared/components/confirmdialog/confirmdialog.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { DepartmentService } from '@shared/services/department.service';
import { EmployeeNoteService, IEmployeeNotesSavedArgs } from '@shared/services/employee-note.service';
import { EmployeeService } from '@shared/services/mesuser.service';
import { NotificationService } from '@shared/services/notification.service';
import { PermissionExpressionService } from '@shared/services/permission-expression.service';

import { UserRoleService } from '@shared/services/role.service';
import { SessionStorageService } from '@shared/services/storage.service';
import { TeamProfileService } from '@shared/services/teamProfiles.service';
import { UserPermissionsService } from '@shared/services/userpermissions.service';
import { WorktaskService } from '@shared/services/worktask.service';
import { catchError, finalize, forkJoin, of, Subject, take, takeUntil } from 'rxjs';
import { CreateShiftNoteComponent } from './create-shift-note/create-shift-note.component';
import { ICreateShiftNoteInput } from './create-shift-note/create-shift-note.models';
import { IMergeShiftsInput, MergeShiftsComponent } from './merge-shifts/merge-shifts.component';
import { MesShiftService } from './mes-shift.service';
import { calculateShiftTimes, formatMinutesAsHMM, resolveBreakMinutes, resolveNormTimeMinutes } from './shift-time-calculator';

const ALL_DEPARTMENTS_SELECTION = '__all_departments__';
const MERGE_NOTE_KEY = 'mes.administration.shift-approval.merge.note';

interface IAllDepartmentsSelection {
	value: typeof ALL_DEPARTMENTS_SELECTION;
}

export type DepartmentSelection = IBaseDepartment | IAllDepartmentsSelection;

export interface ITimestampViewModel {
	id: UUID | null;
	worktask: IWorktask;
	dateTimeOffset: string;
	timestampStatus: string;
	nextTimeStamp: ITimestampViewModel;
}

export interface IShiftListViewModel {
	id: UUID | null;
	date: Date;
	employeeAlias: string;
	employeeName: string;
	teamProfileAlias: string;
	teamProfileName: string;
	department: string;
	rawStartTime: Date;
	rawEndTime: Date;
	correctedStartTime: string;
	correctedEndTime: string;
	payTime: string;
	overtime: string;
	absenceTime: string;
	approved: boolean;
	approvedBy: UUID | null;
	timeStamps: ITimestampViewModel[];
	expandedDetail: string;
	item: IShiftModel;
}

@Component({
	selector: 'app-shift-approval',
	templateUrl: './mes-shift-approval.component.html',
	styleUrls: ['./mes-shift-approval.component.scss'],
	animations: [trigger('detailExpand', [state('collapsed,void', style({ height: '0px', minHeight: '0' })), state('expanded', style({ height: '*' })), transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))])],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ShiftApprovalComponent implements OnInit, AfterViewInit, OnDestroy {
	private destroy$: Subject<void> = new Subject<void>();
	/** The approval view shows shifts on the day they start, the way the backend evaluates them. */
	private readonly dateBasis: ShiftDateBasis = 'Start';
	protected hasCorrectedTimePermission = false;
	protected hasWorktaskSelectPermission = false;
	isLoading = false;
	departments: IBaseDepartment[];
	employees: IMesUser[];
	teamProfiles: ITeamProfile[];
	worktasks: IWorktask[];
	sortedWorktasks: IWorktask[];
	sortedTasks: IWorktask[];
	worktask: IWorktask;
	selectedWorktask: IWorktask | string = '';
	currentDepartment: DepartmentSelection;
	shifts: IShiftModel[];
	showApproved: boolean = true;
	batchRequestExecuting: boolean = false;
	suppressPatchExpansion: boolean = false;
	readonly allDepartmentsSelection: IAllDepartmentsSelection = {
		value: ALL_DEPARTMENTS_SELECTION,
	};

	displayedColumns: string[] = ['select', 'expand', 'date', 'employeeName', 'department', 'teamProfileName', 'rawTime', 'correctedTime', 'payTime', 'overtime', 'absenceTime', 'note', 'approved', 'status'];

	dataSource = new MatTableDataSource<IShiftListViewModel>();

	@ViewChild(MatSort) sort!: MatSort;
	selection = new SelectionModel<IShiftListViewModel>(true, []);
	expandedElement!: IShiftListViewModel | null;

	readonly range = new FormGroup({
		start: new FormControl<Date | null>(null),
		end: new FormControl<Date | null>(null),
	});

	confirmGetShiftsDialogRef: MatDialogRef<ConfirmdialogComponent, any> | null;

	public favoriteItems: SubMenuItem[] = [
		{
			click: () => this.createShiftNote(),
			icon: () => 'acies_add',
			text: () => 'mes.administration.shift-approval.buttons.create-shift-note',
			permissions: () => ['mes:ShiftCreate', 'mes:EmployeeNoteCreate'],
			disableOnMissingPermissions: () => true,
		},
		{
			click: () => this.approveSelectedShifts(),
			disabled: () => !this.isAnyApprovableSelected(),
			icon: () => 'acies_true',
			text: () => 'mes.administration.shift-approval.buttons.approve',
			permissions: () => ['mes:ShiftUpdate'],
			disableOnMissingPermissions: () => true,
		},
		{
			click: () => this.toggleShowHideApproved(),
			icon: () => this.showHideApprovedButtonIcon,
			text: () => this.showHideApprovedButtonText,
		},
	];

	menuItems: SubMenuItem[] = [
		{
			click: () => this.selectValidShifts(),
			icon: () => 'acies_checkmark_simple',
			text: () => 'mes.administration.shift-approval.buttons.select-ok',
		},
		{
			click: () => this.mergeSelectedShifts(),
			disabled: () => !this.isSelectionMergeable(),
			icon: () => 'acies_merge',
			permissions: () => ['mes:ShiftDelete', 'mes:ShiftCreate'],
			text: () => 'mes.administration.shift-approval.buttons.merge',
		},
		{
			click: () => this.unapproveSelectedShifts(),
			disabled: () => !this.isAnyUnapprovableSelected(),
			icon: () => 'acies_false',
			text: () => 'mes.administration.shift-approval.buttons.remove-approval',
			permissions: () => ['mes:ShiftUpdate'],
		},
	];

	constructor(
		private shiftservice: MesShiftService,
		private departmentService: DepartmentService,
		private employeeService: EmployeeService,
		private teamProfileService: TeamProfileService,
		private workTaskService: WorktaskService,
		private translate: TranslateService,
		private notificationService: NotificationService,
		public dialog: MatDialog,
		private userRoleService: UserRoleService,
		private sessionStorageService: SessionStorageService,
		private employeeNoteService: EmployeeNoteService,
		private permissionExpressionService: PermissionExpressionService,
		private userPermissionsService: UserPermissionsService,
	) {
		this.confirmGetShiftsDialogRef = null;
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public departmentChange(event: MatSelectChange): void {
		this.currentDepartment = event.value as DepartmentSelection;
		this.invalidateStartEndDates();
	}

	public invalidateStartEndDates(): void {
		if (this.isStartEndDateMoreThanAMonthApart) {
			this.showPossibleSlowConfirmationDialog();
		} else if (this.areStartEndDatesValid) {
			this.getShifts();
		}
	}

	private showPossibleSlowConfirmationDialog(): void {
		this.confirmGetShiftsDialogRef = this.dialog.open(ConfirmdialogComponent, {
			width: '450px',
			data: {
				okReturnData: false,
				cancelReturnData: false,
				heading: this.translate.instant('mes.shift.approval.warnings.more-than-one-month-selected.header'),
				content: this.translate.instant('mes.shift.approval.warnings.more-than-one-month-selected.content'),
				okButtonText: this.translate.instant('general.ok'),
				cancelButtonText: this.translate.instant('general.cancel'),
			},
		});

		this.confirmGetShiftsDialogRef.afterClosed().subscribe((ok) => {
			if (ok) {
				this.getShifts();
			}
		});
	}

	private get areStartEndDatesValid(): boolean {
		const start: Date | null = this.range.controls.start.value;
		const end: Date | null = this.range.controls.end.value;
		if (start && end && start <= end) {
			return true;
		}
		return false;
	}

	private get isStartEndDateMoreThanAMonthApart(): boolean {
		if (!this.areStartEndDatesValid) {
			return false;
		}

		const start: Date | null = this.range.controls.start.value;
		const end: Date | null = this.range.controls.end.value;
		const THIRTY_TWO_DAYS_IN_MILLISECONDS = 32 * 24 * 60 * 60 * 1000;
		const timeBetweenStartAndEnd = end.getTime() - start.getTime();
		return timeBetweenStartAndEnd > THIRTY_TWO_DAYS_IN_MILLISECONDS;
	}

	ngAfterViewInit() {
		this.dataSource.sort = this.sort;
		this.employeeNoteService.notesSaved.pipe(takeUntil(this.destroy$)).subscribe((c: IEmployeeNotesSavedArgs) => {
			this.notificationService.notify([`mes.employee-notes.note-saved-notify.heading`], [`mes.employee-notes.note-saved-notify.title`, { n: c.succeded.length }], 'success');
			if (c.error.length) {
				this.notificationService.notify([`mes.employee-notes.note-saved-notify.error-heading`], [`mes.employee-notes.note-saved-notify.error-title`, { n: c.error.length }], 'error');
			}
		});
	}

	private get isOnlyMesTeamManager(): boolean {
		const isMesTeamManager = this.userRoleService.userHasRole('MesTeamManager');
		const isMesManager = this.userRoleService.userHasRole('MesManager');
		// MesManagers are also MesTeamManagers, that's why we check for isMesManager === false
		return isMesManager === false && isMesTeamManager === true;
	}

	private filterDepartmentsByShiftApprovers(): void {
		const id: string = this.sessionStorageService.get('User.IAM.Id');
		if (id) {
			this.departments = this.departments.filter((department: IBaseDepartment) => department.shiftApprovers.includes(id));
		}
	}

	private getDepartmentsSuccess(response: IDepartments): void {
		this.departments = response.result;
		if (this.isOnlyMesTeamManager) {
			this.filterDepartmentsByShiftApprovers();
		}
		if (this.departments.length === 0) {
			return;
		}
		this.departments.sort((a, b) => a.alias.toLowerCase().localeCompare(b.alias.toLowerCase()));
		this.currentDepartment = this.departments[0];
		this.initialGetShiftsWhenDataIsReady();
	}

	private getEmployeesSuccess(mesUsers: IMesUsers): void {
		this.employees = mesUsers.result;
		this.initialGetShiftsWhenDataIsReady();
	}

	private getTeamProfilesSuccess(teamProfiles: ITeamProfiles): void {
		this.teamProfiles = teamProfiles.result;
		this.initialGetShiftsWhenDataIsReady();
	}

	private getWorktasksSuccess(worktasks: IWorktasks): void {
		this.worktasks = worktasks.result;
		this.sortedWorktasks = [...worktasks.result].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()) || a.alias.toLowerCase().localeCompare(b.alias.toLowerCase()));
		this.sortedTasks = this.sortedWorktasks.filter((c) => c.id !== WorktaskService.offWorkTaskId);
	}

	ngOnInit(): void {
		this.userPermissionsService.permissions$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.hasCorrectedTimePermission = this.permissionExpressionService.hasPermission(['mes:ShiftUpdate', 'mes:ShiftCreate']);
			this.hasWorktaskSelectPermission = this.permissionExpressionService.hasPermission('mes:ShiftCreate');
		});

		this.departmentService.getDepartments().subscribe(this.getDepartmentsSuccess.bind(this));
		this.employeeService.getEmployees().subscribe(this.getEmployeesSuccess.bind(this));
		this.teamProfileService.getTeamProfiles().subscribe(this.getTeamProfilesSuccess.bind(this));
		this.workTaskService.getWorktasks().subscribe(this.getWorktasksSuccess.bind(this));
		this.shiftservice
			.observeUpdated()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (e) => {
					const updatedShift = this.mapToShiftView(e);
					const index = this.findShiftIndexInDataSource(updatedShift, this.dataSource.data);
					this.dataSource.data[index] = updatedShift;
					this.dataSource._updateChangeSubscription();
					this.setExpandedRow(this.dataSource.data[index]);
				},
			});

		this.shiftservice
			.observePatched()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (e) => {
					const patchedShift = this.mapToShiftView(e);
					const index = this.findShiftIndexInDataSource(patchedShift, this.dataSource.data);
					this.dataSource.data[index] = patchedShift;
					this.dataSource._updateChangeSubscription();
					if (this.batchRequestExecuting || this.suppressPatchExpansion) {
						return;
					}
					this.setExpandedRow(this.dataSource.data[index]);
				},
			});

		this.shiftservice
			.observeDeleted()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (e) => {
					const index = this.dataSource.data.findIndex((d) => d.id === e.id);
					if (index === -1) return;
					const employee = this.employees.find((emp) => emp.id === e.employeeId);
					const departmentId = e.timestamps?.[0]?.departmentId ?? employee?.departmentId;
					// Get dummy shift for deleted shift.
					if (departmentId) {
						this.isLoading = true;
						const shiftDate = new Date(e.correctedStartTime ?? e.rawStartTime);
						const dateStr = `${shiftDate.getFullYear()}-${shiftDate.getMonth() + 1}-${shiftDate.getDate()}`;

						this.shiftservice
							.getByDepartmentAndDate(departmentId as UUID, dateStr, 0, this.dateBasis)
							.pipe(
								take(1),
								finalize(() => {
									this.isLoading = false;
								}),
							)
							.subscribe({
								next: (shifts) => {
									const dummyShift = shifts.find((s) => s.employeeId === e.employeeId && !s.id);
									if (dummyShift) {
										this.dataSource.data[index] = this.mapToShiftView(dummyShift);
									} else {
										this.dataSource.data.splice(index, 1);
									}
									this.dataSource._updateChangeSubscription();
									this.setExpandedRow(null);
								},
								error: () => {
									this.dataSource.data.splice(index, 1);
									this.dataSource._updateChangeSubscription();
									this.setExpandedRow(null);
								},
							});
					} else {
						this.dataSource.data.splice(index, 1);
						this.dataSource._updateChangeSubscription();
						this.setExpandedRow(null);
					}
				},
			});

		this.setSelectedStartEndDateToCurrentDate();
	}

	initialGetShiftsWhenDataIsReady() {
		if (this.dataSource.data?.length > 0 || this.isLoading) return;
		if (this.isGetShiftsDataDependenciesReady) {
			this.getShifts();
		}
	}

	private get isGetShiftsDataDependenciesReady(): boolean {
		return !!this.employees && !!this.teamProfiles && !!this.currentDepartment;
	}

	private get isAllDepartmentsSelected(): boolean {
		return this.currentDepartment === this.allDepartmentsSelection;
	}

	private setSelectedStartEndDateToCurrentDate(): void {
		const today = new Date();
		this.range.controls.start.setValue(today);
		this.range.controls.end.setValue(today);
	}

	private findShiftIndexInDataSource(shift: IShiftListViewModel, shifts: IShiftListViewModel[]): number {
		const index = shifts.findIndex((e) => e.id && e.id === shift.id && e.item.employeeId === shift.item.employeeId);
		if (index === -1) {
			// If we cant find the existing shift, check placeholder shifts (those without ids)
			return shifts.findIndex((e) => !e.id && e.item.employeeId === shift.item.employeeId && this.isSameDay(new Date(e.date), new Date(shift.date)));
		}
		return index;
	}

	private isSameDay(date1: Date, date2: Date): boolean {
		return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
	}

	setExpandedRow(element: IShiftListViewModel) {
		if (!element?.timeStamps[0]?.worktask) {
			this.expandedElement = null;
		} else {
			this.expandedElement = this.expandedElement === element ? null : element;
		}
	}

	canEditCorrectedTimes(shiftListViewModel: IShiftListViewModel): boolean {
		if (shiftListViewModel.approved || shiftListViewModel.item.approved) return false;
		return this.hasOffStamp(shiftListViewModel) || this.isDummyShift(shiftListViewModel);
	}

	shouldShowWorktaskSelector(element: IShiftListViewModel): boolean {
		return !element.timeStamps?.[0]?.worktask;
	}

	private hasOffStamp(shiftListViewModel: IShiftListViewModel): boolean {
		return shiftListViewModel.item.timestamps.some((timestamp) => timestamp.workTaskId === WorktaskService.offWorkTaskId);
	}

	isDummyShift(shiftListViewModel: IShiftListViewModel): boolean {
		return !shiftListViewModel.item.id;
	}

	onUpdateCorrectedTimes(shiftListViewModel: IShiftListViewModel): void {
		if (!this.canEditCorrectedTimes(shiftListViewModel)) return;

		const updateTime = (timeString: string, originalTime: string): string | null => {
			if (!timeString) return null;
			const [hour, minute] = timeString.split(':').map(Number);
			const date = new Date(originalTime);
			if (date.getHours() === hour && date.getMinutes() === minute) return null;
			date.setHours(hour, minute, 0, 0);
			return date.toISOString();
		};

		const correctedStartTime = updateTime(shiftListViewModel.correctedStartTime, shiftListViewModel.item.correctedStartTime) ?? shiftListViewModel.item.correctedStartTime;

		let correctedEndTime = updateTime(shiftListViewModel.correctedEndTime, shiftListViewModel.item.correctedEndTime) ?? shiftListViewModel.item.correctedEndTime;

		if (correctedStartTime && correctedEndTime) {
			const start = new Date(correctedStartTime);
			const end = new Date(correctedEndTime);
			const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);

			if (hours >= 24) {
				end.setDate(end.getDate() - 1);
				correctedEndTime = end.toISOString();
			} else if (hours < 0) {
				end.setDate(end.getDate() + 1);
				correctedEndTime = end.toISOString();
			}
		}

		shiftListViewModel.item.correctedStartTime = correctedStartTime;
		shiftListViewModel.item.correctedEndTime = correctedEndTime;
	}

	onSaveWorktask(shiftListViewModel: IShiftListViewModel, worktask: IWorktask): any {
		shiftListViewModel.item.timestamps[0].workTaskId = worktask.id as UUID;
		this.shiftservice.createShift(shiftListViewModel.item).subscribe({
			next: (c) => {
				const data = [...this.dataSource.data];
				const index = data.findIndex((c) => c.item.employeeId === shiftListViewModel.item.employeeId && c.item.rawStartTime === shiftListViewModel.item.rawStartTime);
				const item = this.mapToShiftView(c.result);

				if (index !== -1) {
					data[index] = { ...data[index], ...item };
					this.dataSource.data = [...data];
					this.setExpandedRow(this.dataSource.data.find((c) => c.item.id === item.item.id));
				}
			},
		});
	}

	onSelectedWorktaskChange(shiftListViewModel: IShiftListViewModel, worktask: IWorktask | string): void {
		if (typeof worktask === 'string') return;
		this.onSaveWorktask(shiftListViewModel, worktask);
	}

	applyFilter(event: Event) {
		const filterValue = (event.target as HTMLInputElement).value;
		this.dataSource.filter = filterValue.trim().toLowerCase();

		const visibleRows = new Set(this.dataSource.filteredData);

		this.selection.selected.filter((row) => !visibleRows.has(row)).forEach((row) => this.selection.deselect(row));
	}

	getShifts() {
		if (!this.isGetShiftsDataDependenciesReady) return;

		this.dataSource.data = [];
		this.isLoading = true;
		this.selection.clear();

		const startDate = this.range.controls.start.value;
		const endDate = this.range.controls.end.value;
		const days = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24));
		const startDateString = `${startDate.getFullYear()}-${startDate.getMonth() + 1}-${startDate.getDate()}`;
		const shiftsRequest = this.isAllDepartmentsSelected
			? this.shiftservice.getByDepartmentsAndDate(
					this.departments.map((department) => department.id as UUID),
					startDateString,
					days,
					this.dateBasis,
				)
			: this.shiftservice.getByDepartmentAndDate((this.currentDepartment as IBaseDepartment).id as UUID, startDateString, days, this.dateBasis);

		shiftsRequest
			.pipe(
				finalize(() => {
					this.isLoading = false;
				}),
			)
			.subscribe({
				next: (c) => {
					this.dataSource.data = c.map((c) => this.mapToShiftView(c)).sort((a, b) => a.date.getTime() - b.date.getTime() || a.rawEndTime.getTime() - b.rawEndTime.getTime() || b.employeeName.localeCompare(a.employeeName));
				},
			});
	}

	createShiftNote(): void {
		const dialogRef = this.dialog.open(CreateShiftNoteComponent, {
			disableClose: true,
			width: '750px',
			data: <ICreateShiftNoteInput>{
				startDate: this.range.controls.start.value,
				endDate: this.range.controls.end.value,
			},
		});
		dialogRef.componentInstance.created.pipe(takeUntil(this.destroy$)).subscribe((_results: any[]) => {
			this.getShifts();
		});
	}

	isSelectionMergeable(): boolean {
		const selected = this.selection.selected;
		return selected.length > 1 && selected.every((shift) => !!shift.id && !shift.approved) && new Set(selected.map((shift) => shift.item.employeeId)).size === 1;
	}

	mergeSelectedShifts(): void {
		const selected = [...this.selection.selected].sort((a, b) => a.rawStartTime.getTime() - b.rawStartTime.getTime());
		const last = selected[selected.length - 1];
		const lastIsOpen = !last.item.timestamps.some((timestamp) => timestamp.workTaskId === WorktaskService.offWorkTaskId);
		const dialogRef = this.dialog.open(MergeShiftsComponent, {
			disableClose: true,
			width: '750px',
			data: <IMergeShiftsInput>{
				shiftIds: selected.map((shift) => shift.id),
				employeeName: selected[0].employeeName,
				start: selected[0].rawStartTime,
				end: lastIsOpen ? null : last.rawEndTime,
				worktasks: this.sortedTasks,
			},
		});
		dialogRef.componentInstance.merged.pipe(takeUntil(this.destroy$)).subscribe((gapWorkTaskId) => {
			this.createMergeNote(selected, gapWorkTaskId);
			this.selection.clear();
			this.getShifts();
		});
	}

	private createMergeNote(sources: IShiftListViewModel[], gapWorkTaskId: UUID | null): void {
		const gapWorkTask = this.worktasks.find((task) => task.id === gapWorkTaskId);
		const key = gapWorkTask ? 'merged-with-worktask' : 'merged';
		const note = this.translate.instant(`${MERGE_NOTE_KEY}.${key}`, {
			count: sources.length,
			worktask: gapWorkTask && `(${gapWorkTask.alias}) ${gapWorkTask.name}`,
		});

		this.employeeNoteService
			.createNote({
				employeeId: sources[0].item.employeeId,
				date: sources[0].date,
				note,
				id: null,
				modifiedDate: null,
				modifiedByUserId: null,
			})
			.pipe(
				takeUntil(this.destroy$),
				catchError(() => of(null)),
			)
			.subscribe((created) => {
				if (created) {
					this.employeeNoteService.onEmployeeNotesChanged({
						employeeId: sources[0].item.employeeId,
						date: sources[0].date,
					});
				}
			});
	}

	approveSelectedShifts(): void {
		const selectedShifts = this.selection.selected;
		const shiftRequests = [];

		selectedShifts
			.filter((s) => !s.approved && this.shiftIsApproveable(s))
			.forEach((s) => {
				this.selection.toggle(s);
				s.item.approved = true;
				shiftRequests.push(
					this.shiftservice.patchShift(s.item).pipe(
						catchError((error) => {
							return of({ error, item: s.item });
						}),
					),
				);
			});

		const batchCall = shiftRequests.length > 1;
		if (shiftRequests.length > 0) {
			this.batchRequestExecuting = batchCall;
			this.suppressPatchExpansion = true;
			forkJoin(shiftRequests)
				.pipe(
					finalize(() => {
						this.batchRequestExecuting = false;
						this.suppressPatchExpansion = false;
					}),
				)
				.subscribe({
					next: (results) => {
						const successes = results.filter((e) => !e.error).length;
						if (successes) {
							this.notificationService.notify(['mes.administration.shift-approval.approve.notifications.success.header'], ['mes.administration.shift-approval.approve.notifications.success.content', { count: successes }], 'success');
						}

						const failures = results.filter((e) => e.error).length;
						if (failures) {
							this.notificationService.notify(['mes.administration.shift-approval.approve.notifications.error.header'], ['mes.administration.shift-approval.approve.notifications.error.content', { count: failures }], 'error');
						}
					},
				});
		}
	}

	unapproveSelectedShifts(): void {
		const selectedShifts = this.selection.selected;
		const shiftRequests = [];

		selectedShifts
			.filter((s) => s.approved && s.id)
			.forEach((s) => {
				this.selection.toggle(s);
				s.item.approved = false;
				shiftRequests.push(
					this.shiftservice.patchShift(s.item).pipe(
						catchError((error) => {
							return of({ error, item: s.item });
						}),
					),
				);
			});

		const batchCall = shiftRequests.length > 1;
		if (shiftRequests.length > 0) {
			this.batchRequestExecuting = batchCall;
			this.suppressPatchExpansion = true;
			forkJoin(shiftRequests)
				.pipe(
					finalize(() => {
						this.batchRequestExecuting = false;
						this.suppressPatchExpansion = false;
					}),
				)
				.subscribe({
					next: (results) => {
						const successes = results.filter((e) => !e.error).length;
						if (successes) {
							this.notificationService.notify(['mes.administration.shift-approval.unapprove.notifications.success.header'], ['mes.administration.shift-approval.unapprove.notifications.success.content', { count: successes }], 'success');
						}

						const failures = results.filter((e) => e.error).length;
						if (failures) {
							this.notificationService.notify(['mes.administration.shift-approval.unapprove.notifications.error.header'], ['mes.administration.shift-approval.unapprove.notifications.error.content', { count: failures }], 'error');
						}
					},
				});
		}
	}

	mapToShiftView(shift: IShiftModel): IShiftListViewModel {
		const employee = this.employees.find((d) => d.id === shift.employeeId);
		const teamProfile = this.teamProfiles.find((d) => d.id === shift.timestamps[0].teamProfileId) ?? null;

		const shiftDate = new Date(shift.correctedStartTime ?? shift.rawStartTime);
		// The row's date is a calendar day, not an instant. Employee notes convert it to UTC, so
		// midday keeps it on the owner date for offsets from UTC-12 to UTC+12.
		const ownerDate = new Date(shiftDate.getFullYear(), shiftDate.getMonth(), shiftDate.getDate(), 12);
		const normTimeMinutes = resolveNormTimeMinutes(teamProfile, shiftDate);
		const breakMinutes = resolveBreakMinutes(teamProfile, shiftDate);
		const shiftTimes = shift.id
			? calculateShiftTimes(
					{
						timestamps: shift.timestamps,
						correctedStartTime: shift.correctedStartTime,
						correctedEndTime: shift.correctedEndTime,
						breakMinutes,
					},
					this.worktasks,
					normTimeMinutes,
				)
			: { payTimeMinutes: 0, overtimeMinutes: 0, absenceMinutes: 0 };

		return <IShiftListViewModel>{
			id: shift.id,
			date: ownerDate,
			employeeAlias: employee.alias,
			employeeName: `${employee.firstName} ${employee.lastName}`,
			teamProfileName: teamProfile?.description ?? '',
			teamProfileAlias: teamProfile?.alias ?? '',
			department: this.departments.find((d) => d.id === shift.timestamps[0].departmentId).alias,
			rawStartTime: new Date(shift.rawStartTime),
			rawEndTime: new Date(shift.rawEndTime),
			correctedStartTime: this.formatTime(shift.correctedStartTime),
			correctedEndTime: this.formatTime(shift.correctedEndTime),
			payTime: formatMinutesAsHMM(shiftTimes.payTimeMinutes),
			overtime: formatMinutesAsHMM(shiftTimes.overtimeMinutes),
			absenceTime: formatMinutesAsHMM(shiftTimes.absenceMinutes),
			timeStamps: this.mapAndChainTimeStamps(shift.timestamps),
			approved: shift.approved,
			approvedBy: shift.approvedBy,
			item: shift,
			expandedDetail: null,
		};
	}

	mapAndChainTimeStamps(timeStamps: ITimestamp[]): ITimestampViewModel[] {
		const timeStampView = new Array<ITimestampViewModel>();
		for (let i = 0; i < timeStamps.length; i++) {
			timeStampView.push(<ITimestampViewModel>{
				dateTimeOffset: timeStamps[i].dateTimeOffset,
				worktask: this.worktasks.find((c) => c.id === timeStamps[i].workTaskId),
				id: timeStamps[i].id,
				timestampStatus: timeStamps[i].timestampStatus ?? 'None',
			});
		}
		for (let i = 0; i < timeStampView.length; i++) {
			timeStampView[i].nextTimeStamp = timeStampView[i + 1];
		}
		return timeStampView;
	}

	formatTime(date: string): string {
		if (date == null) return null;
		const d = new Date(date);
		return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
	}

	selectValidShifts(): void {
		const validShifts = this.dataSource.data.filter(({ item }) => !item.approved && (!item.validationErrors || Object.keys(item.validationErrors).every((key) => key === 'noShiftOnDateValidator')) && item.timestamps.every((ts) => ts.workTaskId));
		this.selection.select(...validShifts);
	}

	shiftIsApproveable(shift: IShiftListViewModel): boolean {
		const timestamps = shift.item.timestamps;
		if (shift.approved) return false;
		if (timestamps.some((e) => !e.workTaskId)) return false;
		if (!timestamps.find((e) => e.workTaskId === WorktaskService.offWorkTaskId)) {
			return false;
		}
		if (timestamps.length < 2) return false;
		const errors = shift.item.validationErrors;
		if (!errors) return true;
		if (Object.keys(errors).length === 0) return true;
		if (errors.shiftNotEndedValidator) return false;
		if (errors.timeOnShiftInsufficientValidator) return false;
		return true;
	}

	private get selectableRows(): IShiftListViewModel[] {
		return this.dataSource.filteredData.filter((row) => !this.isDummyShift(row));
	}

	isAllSelected(): boolean {
		const visibleRows = this.selectableRows;
		const numSelectedVisible = visibleRows.filter((row) => this.selection.isSelected(row)).length;
		return visibleRows.length > 0 && numSelectedVisible === visibleRows.length;
	}

	isSomeSelected(): boolean {
		const visibleRows = this.selectableRows;
		const numSelectedVisible = visibleRows.filter((row) => this.selection.isSelected(row)).length;
		return numSelectedVisible > 0 && numSelectedVisible < visibleRows.length;
	}

	isAnyApprovableSelected(): boolean {
		return this.selection.selected.some((shift) => this.shiftIsApproveable(shift));
	}

	isAnyUnapprovableSelected(): boolean {
		return this.selection.selected.some((shift) => shift.approved);
	}

	toggleAllRows() {
		if (this.isAllSelected()) {
			this.selection.clear();
			return;
		}
		this.selection.select(...this.selectableRows);
	}

	checkboxLabel(shiftListViewModel?: IShiftListViewModel): string {
		const translationKey = 'mes.administration.shift-approval.table.';
		if (!shiftListViewModel) {
			return this.translate.instant(`${translationKey}${this.isAllSelected() ? 'deselect' : 'select'}-all`);
		} else {
			return `${this.translate.instant(`${translationKey}${this.selection.isSelected(shiftListViewModel) ? 'deselect' : 'select'}-row`)} ${shiftListViewModel.correctedEndTime}`;
		}
	}

	toggleShowHideApproved(): void {
		this.showApproved = !this.showApproved;
	}

	public get showHideApprovedButtonText(): string {
		return this.showApproved ? 'mes.administration.shift-approval.buttons.hide-approved' : 'mes.administration.shift-approval.buttons.show-approved';
	}

	public get showHideApprovedButtonIcon(): string {
		return this.showApproved ? 'acies_viewoff' : 'acies_view';
	}
}
